import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { gsap, ScrollTrigger } from "@/animations/gsap";
import { navLinks, profile } from "@/data/profile";
import { scrollToSection, scrollToTop } from "@/lib/scroll";

export function Navigation({ started }: { started: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      navLinks.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return;
        ScrollTrigger.create({
          trigger: el,
          start: "top 45%",
          end: "bottom 45%",
          onToggle: (self) => {
            setActiveSection((cur) => (self.isActive ? id : cur === id ? "" : cur));
          },
        });
      });
    });
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const lenis = window.__lenis;
    if (!lenis) return;
    if (menuOpen) lenis.stop();
    else lenis.start();
  }, [menuOpen]);

  const go = (id: string) => {
    setMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <motion.header
        className={`fixed inset-x-0 top-0 z-[130] transition-colors duration-500 ${scrolled ? "bg-black/45 backdrop-blur-md" : ""}`}
        initial={{ y: -28, opacity: 0 }}
        animate={started ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        data-testid="navigation"
      >
        <div className={`container-x flex items-center justify-between transition-all duration-500 ${scrolled ? "py-3" : "py-6"}`}>
          <button onClick={scrollToTop} className="font-display text-2xl font-bold tracking-[0.22em]" data-testid="nav-logo">
            AMOL
          </button>
          <div className="hidden items-center gap-3 xl:flex" data-testid="nav-status">
            <span className="status-dot" aria-hidden="true" />
            <span className="font-mono text-[10px] tracking-[0.22em] text-muted">{profile.availability}</span>
          </div>
          <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {navLinks.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  go(id);
                }}
                className="link-underline font-mono text-[11px] tracking-[0.22em] transition-colors"
                data-active={activeSection === id}
                data-testid={`nav-link-${id}`}
                style={{ color: activeSection === id ? "var(--foreground)" : "var(--muted)" }}
              >
                {label}
              </a>
            ))}
          </nav>
          <button className="md:hidden" onClick={() => setMenuOpen(true)} aria-label="Open menu" data-testid="nav-menu-button">
            <Menu size={22} aria-hidden="true" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[140] flex flex-col backdrop-blur-xl"
            style={{ backgroundColor: "rgba(5, 6, 10, 0.96)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="container-x flex items-center justify-between py-6">
              <span className="font-display text-2xl font-bold tracking-[0.22em]">AMOL</span>
              <button onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="nav-menu-close">
                <X size={24} aria-hidden="true" />
              </button>
            </div>
            <nav className="container-x mt-10 grid gap-6" aria-label="Mobile">
              {navLinks.map(({ id, label }, i) => (
                <motion.a
                  key={id}
                  href={`#${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    go(id);
                  }}
                  className="font-display text-4xl font-semibold tracking-tight text-foreground"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`mobile-nav-link-${id}`}
                >
                  <span className="mr-4 font-mono text-xs text-accent">{String(i + 1).padStart(2, "0")}</span>
                  {label}
                </motion.a>
              ))}
            </nav>
            <div className="container-x mt-auto pb-10">
              <div className="flex items-center gap-3">
                <span className="status-dot" aria-hidden="true" />
                <span className="font-mono text-[10px] tracking-[0.22em] text-muted">{profile.availability}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
